import React, { useContext, useState, useEffect } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import useCodeIndicators from "../../hooks/useCodeIndicators";
import useProducts from "../products/useProducts";
import NewActionGroup from "../action_groups/NewActionGroup";
import ActionGroupSettings from "../action_groups/ActionGroupSettings";
import ActionsBuilder from "../actions/ActionsBuilder";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const buildPlansFromProductSubscriptions = (element) => {
  const productData = window.cfBuilderProductSubscriptions?.[element.id];
  if (!productData?.subscriptions) return null;

  const { subscriptions, requires_selling_plan } = productData;
  const plans = [];

  if (!requires_selling_plan) {
    plans.push({
      index: "default",
      name:
        element.options["products-subscription-default-plan-text"] ||
        "One-time purchase",
    });
  }

  let counter = 0;
  subscriptions.forEach((subscriptionGroup) => {
    Object.values(subscriptionGroup.sellingPlans).forEach((plan) => {
      const showGroupPrefix = subscriptionGroup.name !== plan.name;
      const planName = showGroupPrefix
        ? `${subscriptionGroup.name} - ${plan.name}`
        : plan.name;
      plans.push({ index: String(counter), name: planName });
      counter++;
    });
  });

  return plans.length > 0 ? plans : null;
};

const SubscriptionCardSettings = ({ element }) => {
  const getPlanPriceVisibility = (planIndex) => {
    const planSettingName = `products-subscription-show-price-${planIndex}`;
    const planValue = Object.prototype.hasOwnProperty.call(
      element.options,
      planSettingName
    )
      ? element.options[planSettingName]
      : undefined;
    if (planValue === "true") return true;
    if (planValue === "false") return false;
    return element.options["products-subscription-show-price"] === "true";
  };

  const getPlanPriceToggleValue = (planIndex) => {
    const planSettingName = `products-subscription-show-price-${planIndex}`;
    const planValue = Object.prototype.hasOwnProperty.call(
      element.options,
      planSettingName
    )
      ? element.options[planSettingName]
      : undefined;
    if (planValue === "true" || planValue === "false") return planValue;
    return element.options["products-subscription-show-price"] === "true"
      ? "true"
      : "false";
  };

  const [subscriptionPlans, setSubscriptionPlans] = useState(() => {
    // Try to build from cached product subscriptions first
    const cached = buildPlansFromProductSubscriptions(element);
    if (cached) return cached;

    // Fall back to inferring from saved options
    const knownPlans = [];
    const opts = element.options || {};

    if (
      opts["products-subscription-hide-default"] !== undefined ||
      opts["products-subscription-description-default"] !== undefined ||
      opts["products-subscription-popular-badge-default"] !== undefined ||
      opts["products-subscription-show-price-default"] !== undefined ||
      opts["products-subscription-compare-price-default"] !== undefined ||
      opts["products-subscription-price-description-default"] !== undefined
    ) {
      knownPlans.push({
        index: "default",
        name:
          opts["products-subscription-default-plan-text"] ||
          "One-time purchase",
      });
    }

    for (let i = 0; i < 10; i++) {
      if (
        opts[`products-subscription-hide-${i}`] !== undefined ||
        opts[`products-subscription-description-${i}`] !== undefined ||
        opts[`products-subscription-popular-badge-${i}`] !== undefined ||
        opts[`products-subscription-badge-text-${i}`] !== undefined ||
        opts[`products-subscription-show-price-${i}`] !== undefined ||
        opts[`products-subscription-compare-price-${i}`] !== undefined ||
        opts[`products-subscription-price-description-${i}`] !== undefined
      ) {
        knownPlans.push({
          index: String(i),
          name: `Subscription Plan ${i + 1}`,
        });
      }
    }
    return knownPlans;
  });

  useEffect(() => {
    const refreshPlans = () => {
      const plans = buildPlansFromProductSubscriptions(element);
      if (plans) {
        setSubscriptionPlans(plans);
      }
    };

    refreshPlans();
    const interval = setInterval(refreshPlans, 2000);
    return () => clearInterval(interval);
  }, [element.id]);

  if (element.options["products-subscription-choices-type"] !== "card")
    return null;

  return (
    <div className="col-xs-12">
      {subscriptionPlans.map((plan) => (
        <VariantBuilderSetting
          key={`subscription-plan-${plan.index}`}
          object={element}
          setting_name={`[options][products-subscription-plan-${plan.index}-position]`}
          setting_type="item_v2"
          object_type="elements"
          object_id={element.id}
          title={plan.name}
          label_icon="fa-arrow-rotate-right"
          label_max_length={30}
          label_hover_max_length={25}
          draggable="false"
          hide_duplicate={true}
          hide_remove={true}
          toggle_setting_name={`[options][products-subscription-hide-${plan.index}]`}
          toggle_value={
            element.options[`products-subscription-hide-${plan.index}`]
              ? element.options[`products-subscription-hide-${plan.index}`]
              : "true"
          }
          panel_type="modal"
          side_panel={true}
          modalTitle={`Settings - ${plan.name}`}
        >
          <div
            className="col-xs-12"
            style={{ paddingLeft: "15px", paddingRight: "15px" }}
          >
            <VariantBuilderSetting
              object={element}
              label="Description"
              setting_name={`[options][products-subscription-description-${plan.index}]`}
              setting_type="editor"
              object_type="elements"
              object_id={element.id}
              value={
                element.options[
                  `products-subscription-description-${plan.index}`
                ]
                  ? element.options[
                      `products-subscription-description-${plan.index}`
                    ]
                  : ""
              }
            />

            <VariantBuilderSetting
              object={element}
              label="Show Badge"
              setting_name={`[options][products-subscription-popular-badge-${plan.index}]`}
              setting_type="checkbox"
              object_type="elements"
              object_id={element.id}
              value={
                element.options[
                  `products-subscription-popular-badge-${plan.index}`
                ]
                  ? element.options[
                      `products-subscription-popular-badge-${plan.index}`
                    ]
                  : "false"
              }
              col="12"
            />

            <VariantBuilderSetting
              object={element}
              label="Badge Text"
              setting_name={`[options][products-subscription-badge-text-${plan.index}]`}
              setting_type="text"
              object_type="elements"
              object_id={element.id}
              value={
                element.options[
                  `products-subscription-badge-text-${plan.index}`
                ]
                  ? element.options[
                      `products-subscription-badge-text-${plan.index}`
                    ]
                  : undefined
              }
              placeholder="MOST POPULAR"
              condition={
                element.options[
                  `products-subscription-popular-badge-${plan.index}`
                ] == "true"
              }
            />

            <VariantBuilderSetting
              object={element}
              label="Show Plan Price"
              setting_name={`[options][products-subscription-show-price-${plan.index}]`}
              setting_type="checkbox"
              object_type="elements"
              object_id={element.id}
              value={getPlanPriceToggleValue(plan.index)}
              col="12"
            />

            <VariantBuilderSetting
              object={element}
              label="Override Price"
              setting_name={`[options][products-subscription-override-price-${plan.index}]`}
              setting_type="text"
              object_type="elements"
              object_id={element.id}
              value={
                element.options[
                  `products-subscription-override-price-${plan.index}`
                ]
                  ? element.options[
                      `products-subscription-override-price-${plan.index}`
                    ]
                  : undefined
              }
              placeholder="$19/mo"
              condition={getPlanPriceVisibility(plan.index)}
              allow_empty={true}
            />

            <VariantBuilderSetting
              object={element}
              label="Price Description"
              setting_name={`[options][products-subscription-price-description-${plan.index}]`}
              setting_type="text"
              object_type="elements"
              object_id={element.id}
              value={
                element.options[
                  `products-subscription-price-description-${plan.index}`
                ]
                  ? element.options[
                      `products-subscription-price-description-${plan.index}`
                    ]
                  : undefined
              }
              placeholder="Per month, billed monthly"
              condition={getPlanPriceVisibility(plan.index)}
            />
          </div>
        </VariantBuilderSetting>
      ))}
    </div>
  );
};

const SubscriptionPreSelectedPicker = ({ element }) => {
  const [plans, setPlans] = useState(
    () => buildPlansFromProductSubscriptions(element) || []
  );

  useEffect(() => {
    const refreshPlans = () => {
      const updated = buildPlansFromProductSubscriptions(element);
      if (updated) {
        setPlans(updated);
      }
    };

    refreshPlans();
    const interval = setInterval(refreshPlans, 2000);
    return () => clearInterval(interval);
  }, [element.id]);

  return (
    <VariantBuilderSetting
      object={element}
      label="Pre-select Plan"
      setting_name="[options][products-subscription-preselected-plan-index]"
      setting_type="dropdown"
      object_type="elements"
      object_id={element.id}
      value={
        element.options["products-subscription-preselected-plan-index"] !==
        undefined
          ? element.options["products-subscription-preselected-plan-index"]
          : ""
      }
      options={[
        { value: "", text: "None" },
        ...plans.map((p) => ({ value: p.index, text: p.name })),
      ]}
    />
  );
};

const ElementSettingsProductContent = (props) => {
  const [contextData, builder, setContextData] = useContext(VariantContextData);
  const { Products } = useProducts();
  const codeIndicators = useCodeIndicators();

  let element = builder.selected().object;
  let variant = contextData.objects.variant;
  let cta = contextData.objects.cta;
  let website = contextData.objects.website;
  let section = variant.sections[element.section_id];
  let step = variant.steps[section.step_id];

  let actionAfterAddToCart = "cart";

  if (element.options["products-button-action-after-add-to-cart"]) {
    actionAfterAddToCart =
      element.options["products-button-action-after-add-to-cart"];
  } else if (element.options["products-cart-redirect"] == "true") {
    actionAfterAddToCart = "cart";
  } else if (element.options["products-cart-redirect"] == "false") {
    actionAfterAddToCart = "stay";
  }

  const reorderContentByPosition = () => {
    let wrapper = $("#options-panel .sortable-container.products-content");
    wrapper
      .find(".setting[data-setting_type='item_v2'][data-draggable='true']")
      .sort(function (a, b) {
        return (
          +a.getAttribute("data-position") - +b.getAttribute("data-position")
        );
      })
      .appendTo(wrapper);
  };

  const contentOrderKey =
    [
      "image",
      "offer-badge",
      "reviews",
      "title",
      "price",
      "description",
      "variant-picker",
      "quantity-picker",
      "subscription",
      "remove-from-cart",
      "button",
    ]
      .map((key) => element.options[`${key}-position`])
      .join(",") +
    "|" +
    Object.values(variant.content_toggle_items)
      .filter(
        (item) => item.element_id == element.id && item.toBeDeleted !== true
      )
      .map((item) => `${item.id}:${item.position}`)
      .join(",");

  useEffect(() => {
    // Make sure the list is ordered by position when the page loads
    reorderContentByPosition();
    Products.sortableContentStart(element);
  }, [element]);

  // Re-apply the order when a position changes from outside the panel (e.g. the
  // canvas right-click move up/down), keeping the settings list in sync.
  useEffect(() => {
    reorderContentByPosition();
  }, [contentOrderKey]);

  const offerBadgeSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[offer-badge-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Offer Badge"
        label_icon={"fa-certificate"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["offer-badge-position"]}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][offer-badge-show]"
        toggle_value={
          element.options["offer-badge-show"]
            ? element.options["offer-badge-show"]
            : "true"
        }
        condition={element.element_type == "product_hero"}
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Show Sold Out Badge"
            setting_name="[options][products-show-image-sold-out-badge]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-show-image-sold-out-badge"]
                ? element.options["products-show-image-sold-out-badge"]
                : "false"
            }
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-image-sold-out-badge-text]"
            setting_type="text"
            label="Sold Out Label"
            object_type="elements"
            object_id={element.id}
            placeholder="SOLD OUT"
            condition={
              element.options["products-show-image-sold-out-badge"] == "true"
            }
            value={
              element.options["products-image-sold-out-badge-text"]
                ? element.options["products-image-sold-out-badge-text"]
                : undefined
            }
            allow_empty={true}
          />

          <VariantBuilderSetting
            object={element}
            label="Badge"
            setting_name="[options][products-image-badge-style]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-image-badge-style"]
                ? element.options["products-image-badge-style"]
                : "show-discount"
            }
            options={[
              {
                value: "show-discount",
                text: "Show Discount",
              },
              {
                value: "custom",
                text: "Custom Message",
              },
            ]}
          />

          <VariantBuilderSetting
            object={element}
            label="Image Badge Custom Message"
            setting_name="[options][products-image-badge-custom-message]"
            setting_type="text"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-image-badge-custom-message"]
                ? element.options["products-image-badge-custom-message"]
                : undefined
            }
            placeholder="10% Off"
            condition={
              element.options["products-reviews-show-label"] !== "false" &&
              element.options["products-image-badge-style"] == "custom"
            }
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const imageSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[image-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Image"
        label_icon={"fa-images"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        draggable={
          element.element_type == "product_hero" ||
          element.options["products-layout"] == "list"
            ? "false"
            : "true"
        }
        position={
          element.options["products-layout"] !== "list"
            ? element.options["image-position"]
            : 1
        }
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][image-show]"
        toggle_value={
          element.options["image-show"] ? element.options["image-show"] : "true"
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Link To Product"
            setting_name="[options][products-image-link-to-product]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-image-link-to-product"]
                ? element.options["products-image-link-to-product"]
                : "false"
            }
            tooltip={"Automatically links to product when button is hidden"}
            tooltip_position="bottom"
            classNames={`no-padding-right`}
            col="12"
          />

          <VariantBuilderSetting
            object={element}
            label="Images to Show"
            setting_name="[options][products-images-to-show]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-images-to-show"]
                ? element.options["products-images-to-show"]
                : "featured-image"
            }
            condition={element.options["products-source"] == "ShopifyGraphql"}
            options={[
              {
                value: "featured-image",
                text: "Just Featured Image",
              },
              {
                value: "carousel",
                text: "Carousel",
              },
            ]}
          />

          <VariantBuilderSetting
            object={element}
            label="Show Image Badge"
            setting_name="[options][products-show-image-badge]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-show-image-badge"]
                ? element.options["products-show-image-badge"]
                : "false"
            }
            col="6"
            classNames="no-padding-right"
            condition={element.element_type !== "product_hero"}
          />

          <VariantBuilderSetting
            object={element}
            label="Show Sold Out Badge"
            setting_name="[options][products-show-image-sold-out-badge]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-show-image-sold-out-badge"]
                ? element.options["products-show-image-sold-out-badge"]
                : "false"
            }
            col="6"
            classNames="no-padding-left"
            condition={element.element_type !== "product_hero"}
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-image-sold-out-badge-text]"
            setting_type="text"
            label="Sold Out Label"
            object_type="elements"
            object_id={element.id}
            placeholder="SOLD OUT"
            condition={
              element.options["products-show-image-sold-out-badge"] == "true" &&
              element.element_type !== "product_hero"
            }
            value={
              element.options["products-image-sold-out-badge-text"]
                ? element.options["products-image-sold-out-badge-text"]
                : undefined
            }
            allow_empty={true}
          />

          <VariantBuilderSetting
            object={element}
            label="Image Badge"
            setting_name="[options][products-image-badge-style]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-image-badge-style"]
                ? element.options["products-image-badge-style"]
                : "show-discount"
            }
            condition={
              element.options["products-show-image-badge"] == "true" &&
              element.element_type !== "product_hero"
            }
            options={[
              {
                value: "show-discount",
                text: "Show Discount",
              },
              {
                value: "custom",
                text: "Custom Message",
              },
            ]}
          />

          <VariantBuilderSetting
            object={element}
            label="Image Badge Custom Message"
            setting_name="[options][products-image-badge-custom-message]"
            setting_type="text"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-image-badge-custom-message"]
                ? element.options["products-image-badge-custom-message"]
                : undefined
            }
            placeholder="10% Off"
            condition={
              element.element_type !== "product_hero" &&
              element.options["products-reviews-show-label"] !== "false" &&
              element.options["products-image-badge-style"] == "custom" &&
              element.options["products-show-image-badge"] == "true"
            }
          />

          <VariantBuilderSetting
            object={element}
            label="Image Badge Placement"
            setting_name="[options][products-image-badge-placement]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-image-badge-placement"]
                ? element.options["products-image-badge-placement"]
                : "top-left"
            }
            condition={
              element.options["products-show-image-badge"] == "true" &&
              element.element_type !== "product_hero"
            }
            options={[
              {
                value: "top-left",
                text: "Top Left",
              },
              {
                value: "top-right",
                text: "Top Right",
              },
              {
                value: "bottom-right",
                text: "Bottom Right",
              },
              {
                value: "bottom-left",
                text: "Bottom Left",
              },
            ]}
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const reviewIntegrationTypes = ReviewService.AVAILABLE_INTEGRATION_TYPES;
  const integrationsList = Object.values(website.integrations).filter(
    (i) =>
      reviewIntegrationTypes.includes(i.integration_type) && i.status == true
  );

  let reviewsSourceOptions = [
    {
      value: "none",
      text: "None",
    },
  ];

  integrationsList.forEach((integration) => {
    reviewsSourceOptions.push({
      value: integration.integration_type,
      text: integration.integration_type,
    });
  });

  const isConvertflowSource =
    !element.options["products-source"] ||
    element.options["products-source"]?.toLowerCase() == "convertflow";

  const reviewSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[reviews-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Reviews"
        label_icon={"fa-star"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["reviews-position"]}
        hide_duplicate={true}
        hide_remove={true}
        condition={element.options["products-source"] == "ShopifyGraphql"}
        toggle_setting_name="[options][reviews-show]"
        toggle_value={
          element.options["reviews-show"]
            ? element.options["reviews-show"]
            : "false"
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Reviews Source"
            setting_name="[options][products-reviews-source]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-reviews-source"]
                ? element.options["products-reviews-source"]
                : "none"
            }
            callback={(value) => {
              window.cf_product_reviews_source = value;
            }}
            options={reviewsSourceOptions}
          />

          <VariantBuilderSetting
            object={element}
            label="Reviews"
            setting_name="[options][reviews-ids]"
            setting_type="select"
            object_type="elements"
            object_id={element.id}
            value={element.options["reviews-ids"]}
            condition={
              !element.options["products-reviews-source"] ||
              element.options["products-reviews-source"] == "ConvertFlow"
            }
            options={Object.values(website.reviews).map((review) => ({
              value: review.id.toString(),
              text:
                review.customer_name && review.title
                  ? `${review.customer_name} - ${review.title}`
                  : review.customer_name
                  ? review.customer_name
                  : review.title
                  ? review.title
                  : `Review ${review.id}`,
            }))}
            multi_select={true}
            allow_empty={true}
          />

          <VariantBuilderSetting
            object={element}
            label="Show Count"
            setting_name="[options][products-reviews-show-count]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-reviews-show-count"]
                ? element.options["products-reviews-show-count"]
                : "true"
            }
            col="6"
          />

          <VariantBuilderSetting
            object={element}
            label="Show Label"
            setting_name="[options][products-reviews-show-label]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-reviews-show-label"]
                ? element.options["products-reviews-show-label"]
                : "true"
            }
            col="6"
          />

          <VariantBuilderSetting
            object={element}
            label="Label"
            setting_name="[options][products-reviews-label]"
            setting_type="text"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-reviews-label"]
                ? element.options["products-reviews-label"]
                : undefined
            }
            placeholder="Reviews"
            condition={
              element.options["products-reviews-show-label"] !== "false"
            }
          />

          <VariantBuilderSetting
            object={element}
            label="Link To Product"
            setting_name="[options][products-reviews-link-to-product]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-reviews-link-to-product"]
                ? element.options["products-reviews-link-to-product"]
                : "false"
            }
            tooltip={"Automatically links to product when button is hidden"}
            tooltip_position="bottom"
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const titleSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[title-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Title"
        label_icon={"fa-a"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["title-position"]}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][title-show]"
        toggle_value={
          element.options["title-show"] ? element.options["title-show"] : "true"
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Max Number of Lines"
            setting_name="[options][products-title-max-number-of-lines]"
            setting_type="range"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-title-max-number-of-lines"]
                ? element.options["products-title-max-number-of-lines"]
                : undefined
            }
            allow_empty={true}
            placeholder={
              variant.options["products-title-max-number-of-lines"]
                ? variant.options["products-title-max-number-of-lines"]
                : "auto"
            }
            min={1}
            max={100}
            device="desktop"
            range_step={1}
          />

          <VariantBuilderSetting
            object={element}
            label="Max Number of Lines"
            setting_name="[options][products-title-max-number-of-lines-mobile]"
            setting_type="range"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-title-max-number-of-lines-mobile"]
                ? element.options["products-title-max-number-of-lines-mobile"]
                : undefined
            }
            allow_empty={true}
            placeholder={
              variant.options["products-title-max-number-of-lines-mobile"]
                ? variant.options["products-title-max-number-of-lines-mobile"]
                : "auto"
            }
            min={1}
            max={100}
            device="mobile"
            range_step={1}
          />

          <VariantBuilderSetting
            object={element}
            label="Link To Product"
            setting_name="[options][products-title-link-to-product]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-title-link-to-product"]
                ? element.options["products-title-link-to-product"]
                : "false"
            }
            tooltip={"Automatically links to product when button is hidden"}
            tooltip_position="bottom"
            classNames={`no-padding-right`}
          />

          <VariantBuilderSetting
            object={element}
            label="Show Variant Name"
            setting_name="[options][products-title-show-variant-name]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-title-show-variant-name"]
                ? element.options["products-title-show-variant-name"]
                : "true"
            }
            col="12"
            condition={
              element.options["confirmation-type"] == "cart" ||
              element.options["shopify-products-source"] == "cart" ||
              (element.options["products-source"] == "ShopifyGraphql" &&
                !element.options["shopify-products-source"])
            }
          />

          <VariantBuilderSetting
            key={`${element.id}-products-title-override-text-${element.options["products-title-override-text"]}`}
            object={element}
            label="Override Title Text"
            setting_name="[options][products-title-override-text]"
            setting_type="text"
            object_type="elements"
            object_id={element.id}
            placeholder="Type something"
            value={
              element.options["products-title-override-text"]
                ? element.options["products-title-override-text"]
                : undefined
            }
            condition={element.element_type == "product_hero"}
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-title-override-text]"
            setting_type="remix_content"
            object_type="elements"
            object_id={element.id}
            value={element.options["products-title-override-text"]}
            condition={element.element_type == "product_hero"}
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const descriptionSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[description-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Description"
        label_icon={"fa-align-left"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["description-position"]}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][description-show]"
        toggle_value={
          // handle legacy values
          element.options["description-show"]
            ? element.options["description-show"]
            : element.options["products-descriptions"]
            ? element.options["products-descriptions"]
            : "true"
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Formatting"
            setting_name="[options][products-description-formatting]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-description-formatting"]
                ? element.options["products-description-formatting"]
                : undefined
            }
            options={[
              { value: "", text: "Default" },
              { value: "cf-unformatted-text", text: "Unformatted Text" },
            ]}
          />

          <VariantBuilderSetting
            object={element}
            label="Max Number of Lines"
            setting_name="[options][products-description-max-number-of-lines]"
            setting_type="range"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-description-max-number-of-lines"]
                ? element.options["products-description-max-number-of-lines"]
                : undefined
            }
            allow_empty={true}
            placeholder={
              variant.options["products-description-max-number-of-lines"]
                ? variant.options["products-description-max-number-of-lines"]
                : "auto"
            }
            min={1}
            max={100}
            device="desktop"
            range_step={1}
          />

          <VariantBuilderSetting
            object={element}
            label="Max Number of Lines"
            setting_name="[options][products-description-max-number-of-lines-mobile]"
            setting_type="range"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-description-max-number-of-lines-mobile"]
                ? element.options[
                    "products-description-max-number-of-lines-mobile"
                  ]
                : undefined
            }
            allow_empty={true}
            placeholder={
              variant.options["products-description-max-number-of-lines-mobile"]
                ? variant.options[
                    "products-description-max-number-of-lines-mobile"
                  ]
                : "auto"
            }
            min={1}
            max={100}
            device="mobile"
            range_step={1}
          />

          <VariantBuilderSetting
            object={element}
            label="Link To Product"
            setting_name="[options][products-description-link-to-product]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-description-link-to-product"]
                ? element.options["products-description-link-to-product"]
                : "false"
            }
            tooltip={"Automatically links to product when button is hidden"}
            tooltip_position="bottom"
            classNames={`no-padding-right`}
          />

          <VariantBuilderSetting
            object={element}
            label="Override Description Text"
            setting_name="[options][products-description-override-text]"
            setting_type="editor"
            object_type="elements"
            object_id={element.id}
            editor_live_preview={false}
            allow_empty={true}
            value={
              element.options["products-description-override-text"]
                ? element.options["products-description-override-text"]
                : undefined
            }
            condition={element.element_type == "product_hero"}
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const priceSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[price-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Price"
        label_icon={"fa-dollar-sign"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["price-position"]}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][price-show]"
        toggle_value={
          element.options["price-show"] ? element.options["price-show"] : "true"
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-discount-type]"
            setting_type="dropdown"
            label="Discount To Show"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-discount-type"]
                ? element.options["products-discount-type"]
                : ""
            }
            options={[
              { value: "", text: "No discount" },
              { value: "amount", text: "Amount" },
              { value: "percentage", text: "Percentage" },
              {
                value: "detect",
                text: "Difference with product sale price",
              },
            ]}
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-discount-amount]"
            setting_type="number"
            label="Discount Amount"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-discount-amount"]
                ? element.options["products-discount-amount"]
                : "10"
            }
            allow_empty={true}
            min={1}
            condition={element.options["products-discount-type"] == "amount"}
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-discount-percentage]"
            setting_type="range"
            label="Discount Percentage"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-discount-percentage"]
                ? element.options["products-discount-percentage"]
                : "10"
            }
            min={1}
            max={100}
            condition={
              element.options["products-discount-type"] == "percentage"
            }
          />

          <VariantBuilderSetting
            object={element}
            label="Link To Product"
            setting_name="[options][products-price-link-to-product]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-price-link-to-product"]
                ? element.options["products-price-link-to-product"]
                : "false"
            }
            tooltip={"Automatically links to product when button is hidden"}
            tooltip_position="bottom"
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const variantPickerSettings = () => {
    let styleOptions = [{ value: "dropdown", text: "Dropdown" }];

    if (element.options["products-source"] == "ShopifyGraphql") {
      styleOptions.push({
        value: "cf-options-buttons",
        text: "Options Buttons",
      });
    }

    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[variant-picker-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Variant Picker"
        label_icon={"fa-ellipsis"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["variant-picker-position"]}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][variant-picker-show]"
        toggle_disabled={element.options["products-source"] == "Shopify"}
        toggle_value={
          element.options["products-source"] == "Shopify"
            ? "true"
            : element.options["variant-picker-show"]
            ? element.options["variant-picker-show"]
            : element.options["confirmation-type"] == "product" &&
              element.options["products-source"] == "ShopifyGraphql"
            ? "false"
            : "true"
        }
        condition={
          element.options["confirmation-type"] == "cart" ||
          isConvertflowSource ||
          (element.options["confirmation-type"] == "product" &&
            element.options["products-source"] == "ShopifyGraphql")
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Show Label"
            setting_name="[options][products-variant-picker-show-label]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-variant-picker-show-label"]
                ? element.options["products-variant-picker-show-label"]
                : "false"
            }
            col="12"
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-variant-picker-style]"
            setting_type="dropdown"
            label="Variant Picker Style"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-variant-picker-style"]
                ? element.options["products-variant-picker-style"]
                : "dropdown"
            }
            options={styleOptions}
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const quantityPickerSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[quantity-picker-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Quantity"
        label_icon={"fa-square-plus"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["quantity-picker-position"]}
        hide_duplicate={true}
        hide_remove={true}
        condition={
          ["Shopify", "ShopifyGraphql"].includes(
            element.options["products-source"]
          ) &&
          (element.options["confirmation-type"] == "cart" ||
            !element.options["shopify-products-source"] ||
            element.options["shopify-products-source"] == "cart")
        }
        toggle_setting_name="[options][quantity-picker-show]"
        toggle_value={
          // handle legacy values
          element.options["quantity-picker-show"]
            ? element.options["quantity-picker-show"]
            : element.options["products-quantity"]
            ? element.options["products-quantity"]
            : "false"
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Show Label"
            setting_name="[options][products-quantity-picker-show-label]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-quantity-picker-show-label"]
                ? element.options["products-quantity-picker-show-label"]
                : "false"
            }
            col="12"
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-quantity-label-text]"
            setting_type="text"
            label="Label Text"
            object_type="elements"
            object_id={element.id}
            placeholder="Quantity"
            value={
              element.options["products-quantity-label-text"]
                ? element.options["products-quantity-label-text"]
                : undefined
            }
            allow_empty={true}
          />

          <VariantBuilderSetting
            object={element}
            label="Default Quantity"
            setting_name="[options][quantity-default-source]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["quantity-default-source"]
                ? element.options["quantity-default-source"]
                : "static"
            }
            options={[
              { value: "static", text: "Static" },
              { value: "field", text: "From custom field" },
              { value: "window", text: "From window variable" },
            ]}
          />

          <VariantBuilderSetting
            object={element}
            label="Quantity Value"
            setting_name="[options][quantity-default-static-value]"
            setting_type="number"
            object_type="elements"
            object_id={element.id}
            placeholder="1"
            value={
              element.options["quantity-default-static-value"]
                ? element.options["quantity-default-static-value"]
                : undefined
            }
            condition={
              !element.options["quantity-default-source"] ||
              element.options["quantity-default-source"] === "static"
            }
          />

          <VariantBuilderSetting
            object={element}
            label="Custom field"
            setting_name="[options][quantity-default-field]"
            setting_type="select"
            object_type="elements"
            object_id={element.id}
            placeholder="Select a custom field"
            value={
              element.options["quantity-default-field"]
                ? element.options["quantity-default-field"]
                : undefined
            }
            options={(() => {
              let groups = [];

              let cfGroup = {
                label: "Saved Custom Fields",
                options: [],
              };

              const allFields = [
                ...Object.values(website.fields || {}),
                ...Object.values(variant.fields || {}),
              ];

              allFields.forEach((f) => {
                if (
                  f.slug &&
                  f.slug !== "New Field" &&
                  f.slug !== "" &&
                  cfGroup.options.filter((o) => o.value === f.slug).length ===
                    0 &&
                  (!f.service ||
                    f.service === "ConvertFlow" ||
                    !website.integrations ||
                    Object.values(website.integrations).filter(
                      (i) => i.integration_type === f.service
                    ).length === 0)
                ) {
                  cfGroup.options.push({
                    value: f.slug,
                    label: f.slug,
                    icon: contextData.logo,
                  });
                }
              });

              groups.push(cfGroup);

              if (website.integrations) {
                Object.values(website.integrations)
                  .filter(
                    (integration) =>
                      integration.features &&
                      integration.features.includes("contact")
                  )
                  .sort((a, b) =>
                    a.integration_type.localeCompare(b.integration_type)
                  )
                  .forEach((integration) => {
                    let serviceGroup = {
                      label: integration.integration_type,
                      options: [],
                    };

                    const properties =
                      integration.api_data &&
                      (integration.api_data["properties"] ||
                        integration.api_data["fields"]);
                    if (properties) {
                      Object.values(properties).forEach((property) => {
                        serviceGroup.options.push({
                          value: property.id,
                          label: property.name,
                          icon: `/images/integrations/${
                            integration.icon
                              ? integration.icon.split("/").pop()
                              : null
                          }`,
                        });
                      });
                    } else {
                      serviceGroup.options.push({
                        label: `${integration.integration_type} automatically receives ConvertFlow's saved custom fields`,
                        isDisabled: true,
                        icon: `/images/integrations/${
                          integration.icon
                            ? integration.icon.split("/").pop()
                            : null
                        }`,
                      });
                    }

                    groups.push(serviceGroup);
                  });
              }

              return groups;
            })()}
            condition={element.options["quantity-default-source"] === "field"}
          />

          <VariantBuilderSetting
            object={element}
            label="Window Variable"
            setting_name="[options][quantity-default-window-var]"
            setting_type="text"
            object_type="elements"
            object_id={element.id}
            placeholder="e.g. myApp.recommendedQty"
            value={
              element.options["quantity-default-window-var"]
                ? element.options["quantity-default-window-var"]
                : undefined
            }
            condition={element.options["quantity-default-source"] === "window"}
          />
        </div>
      </VariantBuilderSetting>
    );
  };

  const subscriptionSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[subscription-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Subscriptions"
        label_icon={"fa-arrow-rotate-right"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["subscription-position"]}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][subscription-show]"
        condition={
          element.options["products-source"] == "ShopifyGraphql" &&
          element.options["confirmation-type"] == "cart"
        }
        toggle_value={
          element.options["subscription-show"]
            ? element.options["subscription-show"]
            : "false"
        }
      >
        <div className="col-xs-12">
          <VariantBuilderSetting
            object={element}
            label="Show Label"
            setting_name="[options][products-subscription-show-label]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-show-label"]
                ? element.options["products-subscription-show-label"]
                : "true"
            }
            col="12"
          />

          <VariantBuilderSetting
            object={element}
            label="Show Subscription Group Name"
            setting_name="[options][products-subscription-show-group-name]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-show-group-name"]
                ? element.options["products-subscription-show-group-name"]
                : "true"
            }
            col="12"
          />

          <VariantBuilderSetting
            object={element}
            label="Show Plan Price"
            setting_name="[options][products-subscription-show-price]"
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-show-price"]
                ? element.options["products-subscription-show-price"]
                : "false"
            }
            col="12"
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-subscription-price-type]"
            setting_type="dropdown"
            label="Plan Price Discount To Show"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-price-type"]
                ? element.options["products-subscription-price-type"]
                : ""
            }
            options={[
              { value: "", text: "No discount" },
              { value: "amount", text: "Amount" },
              { value: "percentage", text: "Percentage" },
              {
                value: "detect",
                text: "Difference with product sale price",
              },
            ]}
            condition={
              element.options["products-subscription-show-price"] == "true"
            }
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-subscription-discount-amount]"
            setting_type="number"
            label="Plan Price Discount Amount"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-discount-amount"]
                ? element.options["products-subscription-discount-amount"]
                : "10"
            }
            allow_empty={true}
            min={1}
            condition={
              element.options["products-subscription-show-price"] == "true" &&
              element.options["products-subscription-price-type"] == "amount"
            }
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-subscription-discount-percentage]"
            setting_type="range"
            label="Plan Price Discount Percentage"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-discount-percentage"]
                ? element.options["products-subscription-discount-percentage"]
                : "10"
            }
            min={1}
            max={100}
            condition={
              element.options["products-subscription-show-price"] == "true" &&
              element.options["products-subscription-price-type"] ==
                "percentage"
            }
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-subscription-label-text]"
            setting_type="text"
            label="Label Text"
            object_type="elements"
            object_id={element.id}
            placeholder="Frequency"
            value={
              element.options["products-subscription-label-text"]
                ? element.options["products-subscription-label-text"]
                : undefined
            }
            allow_empty={true}
          />

          <VariantBuilderSetting
            object={element}
            label="Default Plan Text"
            setting_name="[options][products-subscription-default-plan-text]"
            setting_type="text"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-default-plan-text"]
                ? element.options["products-subscription-default-plan-text"]
                : undefined
            }
            placeholder="One-time purchase"
          />

          {element.element_type == "product_hero" && (
            <SubscriptionPreSelectedPicker element={element} />
          )}

          <VariantBuilderSetting
            object={element}
            setting_name="[options][products-subscription-choices-type]"
            setting_type="dropdown"
            label="Plan Selector Type"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["products-subscription-choices-type"]
                ? element.options["products-subscription-choices-type"]
                : undefined
            }
            options={[
              { value: "dropdown", text: "Dropdown" },
              { value: "radios", text: "Radios" },
              { value: "card", text: "Card" },
            ]}
          />

          <SubscriptionCardSettings element={element} />
        </div>
      </VariantBuilderSetting>
    );
  };

  let productsButtonActionAfterAddToCartOptions = [
    {
      value: "cart",
      text: "Redirect to cart",
    },
  ];

  if (
    element.options["products-source"] == "ShopifyGraphql" ||
    element.options["products-source"] == "Shopify"
  ) {
    productsButtonActionAfterAddToCartOptions.push(
      { value: "checkout", text: "Redirect to checkout" },
      { value: "redirect", text: "Redirect to a URL" },
      { value: "step", text: "Jump to a funnel step" },
      { value: "cta", text: "Display a popup campaign" }
    );
  }

  let redirectSteps;
  if (element.options["products-button-action-after-add-to-cart"] == "step") {
    redirectSteps = [
      {
        value: "",
        text: "Select a step",
      },
    ];

    Object.values(variant.steps)
      .filter((s) => s.id !== step.id && s.toBeDeleted !== true)
      .sort((a, b) => {
        return a.position - b.position;
      })
      .forEach((s) => {
        redirectSteps.push({
          value: s.id + "",
          text:
            "Step " +
            s.position +
            (s.options["name"] ? " - " + s.options["name"] : ""),
        });
      });

    if (step.position > 1) {
      redirectSteps.push({
        value: "previous",
        text: "Jump back to previous step",
      });
    }
  }

  const removeFromCartSettings = () => {
    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[remove-from-cart-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Remove"
        label_icon={"fa-trash"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["remove-from-cart-position"]}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][remove-from-cart-show]"
        toggle_value={
          element.options["remove-from-cart-show"]
            ? element.options["remove-from-cart-show"]
            : "false"
        }
        condition={
          element.element_type == "products" &&
          element.options["products-source"] == "ShopifyGraphql" &&
          (!element.options["shopify-products-source"] ||
            element.options["shopify-products-source"] == "cart")
        }
      >
        <VariantBuilderSetting
          object={element}
          setting_name="[options][products-remove-from-cart-text]"
          setting_type="text"
          label="Remove From Cart Text"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["products-remove-from-cart-text"]
              ? element.options["products-remove-from-cart-text"]
              : ""
          }
          placeholder={"Remove"}
        />
      </VariantBuilderSetting>
    );
  };

  const buttonSettings = () => {
    const action_groups = Object.values(variant.action_groups).filter(
      (action_group) =>
        action_group.element_id == element.id &&
        action_group.toBeDeleted !== true
    );

    return (
      <VariantBuilderSetting
        object={element}
        setting_name="[button-position]"
        setting_type="item_v2"
        object_type="elements"
        object_id={element.id}
        title="Button"
        label_icon={"fa-arrow-pointer"}
        label_max_length={40}
        label_hover_max_length={30}
        style={{ margin: "4px 0px" }}
        position={element.options["button-position"]}
        onEdit={() => {
          dispatchCustomEvent("selectObject", {
            object_type: "elements",
            object_id: element.id,
            tab: "Actions",
          });

          setTimeout(() => {
            const button = $(
              '.setting[data-setting_name="[default-action]"] .cf-item-controls button[title="Edit"]'
            )[0];

            var event = new MouseEvent("click", {
              view: window,
              bubbles: true,
              cancelable: true,
            });

            button.dispatchEvent(event);
          }, 500);
        }}
        hide_duplicate={true}
        hide_remove={true}
        toggle_setting_name="[options][button-show]"
        toggle_disabled={
          element.options["products-source"] == "Shopify" &&
          element.options["confirmation-type"] == "cart"
        }
        toggle_value={
          // handle legacy values
          element.options["products-source"] == "Shopify" &&
          element.options["confirmation-type"] == "cart"
            ? "true"
            : element.options["button-show"]
            ? element.options["button-show"]
            : element.options["products-button"]
            ? element.options["products-button"]
            : "true"
        }
      >
        <VariantBuilderSetting
          object={element}
          label="Upon button click"
          setting_name="[options][confirmation-type]"
          setting_type="dropdown"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["confirmation-type"]
              ? element.options["confirmation-type"]
              : ""
          }
          options={[
            {
              value: "product",
              text: "Redirect to product",
            },
            ...(["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            )
              ? [
                  {
                    value: "cart",
                    text: "Add product to cart",
                  },
                ]
              : []),
          ]}
          note={
            ["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            ) && element.options["confirmation-type"] == "cart"
              ? "The product selected will be added to cart automatically"
              : isConvertflowSource ||
                element.options["confirmation-type"] == "product"
              ? "Visitors will be automatically redirected to the selected product"
              : ""
          }
        />

        <VariantBuilderSetting
          object={element}
          setting_name="[html]"
          setting_type="editor"
          label="Button Text"
          object_type="elements"
          object_id={element.id}
          value={
            element.html
              ? element.html
              : element.options["confirmation-type"] == "cart" &&
                element.options["products-source"]?.toLowerCase() !==
                  "convertflow"
              ? "<p>Add To Cart</p>"
              : "<p>Go To Product</p>"
          }
        />

        <VariantBuilderSetting
          object={element}
          label="Sold Out Product Button Text"
          setting_name="[options][products-sold-out-button-text]"
          setting_type="text"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["products-sold-out-button-text"]
              ? element.options["products-sold-out-button-text"]
              : undefined
          }
          placeholder="Sold Out"
          condition={
            ["ShopifyGraphql"].includes(element.options["products-source"]) &&
            element.options["confirmation-type"] !== "product"
          }
        />

        <VariantBuilderSetting
          object={element}
          label={"Track Conversion When"}
          setting_name="[options][track-conversion-when]"
          setting_type="dropdown"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["track-conversion-when"]
              ? element.options["track-conversion-when"]
              : "cf-added-to-cart"
          }
          condition={
            element.options["confirmation-type"] !== "product" &&
            ["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            )
          }
          options={[
            { value: "cf-added-to-cart", text: "Product added to cart" },
            {
              value: "cf-link-clicked-or-added-to-cart",
              text: "Product link clicked or added to cart",
            },
            {
              value: "cf-clicking-through-to-cart",
              text: "Clicking through after adding to cart",
            },
          ]}
        />

        <ActionsBuilder
          cta={cta}
          variant={variant}
          website={website}
          contactable_type="Element"
          contactable_id={element.id}
          element={element}
          title={"Trigger These Automations"}
        />

        <VariantBuilderSetting
          object={element}
          label="Fire a script"
          setting_name="[options][script]"
          setting_type="code"
          object_type="elements"
          object_id={element.id}
          indicators={codeIndicators.get("elements", element.id, {
            type: "custom_code",
          })}
          value={element.options["script"] ? element.options["script"] : ""}
          tooltip={
            element.element_type == "form"
              ? "Custom scripts will fire upon successful submission of form, before redirect actions are taken"
              : element.element_type == "button"
              ? "Custom scripts will fire upon clicking button, before redirect actions are taken"
              : element.element_type == "survey"
              ? "Custom scripts will fire upon selecting an option, before redirect actions are taken"
              : ""
          }
          feature_gate_type={"Custom Scripts"}
        />

        <React.Fragment>
          <div
            className="field-title col-xs-12"
            style={{ margin: "15px 0px 0px" }}
          >
            Run These Conditional Actions
          </div>

          {action_groups.map((action_group) => {
            return (
              <ActionGroupSettings
                key={"action-group-" + action_group.id}
                step={step}
                element={element}
                action_group={action_group}
              />
            );
          })}
          <NewActionGroup element={element} />
        </React.Fragment>

        <VariantBuilderSetting
          object={element}
          label="Redirect to cart upon add to cart"
          setting_name="[options][products-cart-redirect]"
          setting_type="checkbox"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["products-cart-redirect"]
              ? element.options["products-cart-redirect"]
              : "false"
          }
          condition={
            element.element_type == "products" &&
            ["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            ) &&
            element.options["confirmation-type"] == "cart"
          }
          note="Leave this untoggled if you want shoppers to be able to add multiple products to cart"
        />

        {element.options["confirmation-type"] !== "product" &&
          element.options["products-cart-redirect"] !== "true" && (
            <div className="field-headline col-xs-12 some-vertical-space">
              After adding to cart, have the button:
            </div>
          )}

        <VariantBuilderSetting
          object={element}
          label="Button Text After Add To Cart"
          setting_name="[options][products-cart-button]"
          setting_type="editor"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["products-cart-button"]
              ? element.options["products-cart-button"]
              : "<p>View Cart</p>"
          }
          condition={
            element.options["products-cart-redirect"] !== "true" &&
            ["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            ) &&
            element.options["confirmation-type"] !== "product"
          }
        />

        <VariantBuilderSetting
          object={element}
          label="Upon button click"
          setting_name="[options][products-button-action-after-add-to-cart]"
          setting_type="dropdown"
          object_type="elements"
          object_id={element.id}
          value={actionAfterAddToCart}
          condition={
            element.options["products-cart-redirect"] !== "true" &&
            ["ShopifyGraphql", "Shopify"].includes(
              element.options["products-source"]
            ) &&
            element.options["confirmation-type"] == "cart"
          }
          options={productsButtonActionAfterAddToCartOptions}
        />

        <VariantBuilderSetting
          object={element}
          label="Default funnel step to jump to"
          setting_name="[options][products-button-redirect-step]"
          setting_type="dropdown"
          object_type="elements"
          object_id={element.id}
          value={
            ElementWarningsService.isValidRedirectStep(
              element.options["products-button-redirect-step"],
              variant,
              step?.id
            )
              ? element.options["products-button-redirect-step"]
              : ""
          }
          options={redirectSteps}
          condition={
            element.options["confirmation-type"] == "cart" &&
            element.options["products-cart-redirect"] !== "true" &&
            element.options["products-button-action-after-add-to-cart"] ==
              "step"
          }
          warning={
            !ElementWarningsService.isValidRedirectStep(
              element.options["products-button-redirect-step"],
              variant,
              step?.id
            )
              ? "Step to jump to not yet set"
              : undefined
          }
        />

        <VariantBuilderSetting
          object={element}
          label="Redirect to a URL"
          setting_name="[options][products-button-redirect-url]"
          setting_type="text"
          object_type="elements"
          object_id={element.id}
          value={element.options["products-button-redirect-url"]}
          condition={
            element.options["confirmation-type"] == "cart" &&
            element.options["products-cart-redirect"] !== "true" &&
            element.options["products-button-action-after-add-to-cart"] ==
              "redirect"
          }
          placeholder="https://"
          warning={
            !element.options["products-button-redirect-url"]
              ? "Redirect URL not yet set"
              : undefined
          }
        />

        <VariantBuilderSetting
          object={element}
          label="Campaign to trigger"
          setting_name="[options][products-button-confirmation-cta]"
          setting_type="dropdown"
          object_type="elements"
          object_id={element.id}
          value={element.options["products-button-confirmation-cta"]}
          options={[
            {
              value: "",
              text: "Select a popup campaign",
              disabled: true,
            },
            ...Object.values(website.ctas)
              .filter(
                (c) =>
                  ["inline", "page"].includes(c.cta_type) == false &&
                  c.id !== cta.id
              )
              .sort((a, b) => a.name?.localeCompare(b.name))
              .map((c) => {
                return {
                  value: c.id,
                  text:
                    (c.name || "Campaign " + c.id) +
                    " – " +
                    c.cta_type?.substr(0, 1)?.toUpperCase() +
                    c.cta_type?.substr(1),
                };
              }),
          ]}
          condition={
            element.options["confirmation-type"] == "cart" &&
            element.options["products-cart-redirect"] !== "true" &&
            element.options["products-button-action-after-add-to-cart"] == "cta"
          }
          tooltip="Enabling this will trigger the select popup campaign"
          warning={
            !element.options["products-button-confirmation-cta"]
              ? "Campaign to display not yet set"
              : undefined
          }
        />

        <VariantBuilderSetting
          object={element}
          label="Close current popup"
          setting_name="[options][products-button-close-popup]"
          setting_type="checkbox"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["products-button-close-popup"]
              ? element.options["products-button-close-popup"]
              : "false"
          }
          condition={
            element.options["products-button-action-after-add-to-cart"] ==
              "cta" &&
            ["overlay", "hook", "bar"].includes(cta.cta_type) &&
            ["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            ) &&
            element.options["confirmation-type"] == "cart"
          }
        />

        <VariantBuilderSetting
          object={element}
          label="Open in new tab?"
          setting_name="[options][target-blank]"
          setting_type="checkbox"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["target-blank"]
              ? element.options["target-blank"]
              : "false"
          }
          condition={
            ["redirect", "product", "cart"].includes(
              element.options["confirmation-type"]
            ) &&
            (element.options["confirmation-type"] !== "cart" ||
              (actionAfterAddToCart == "cart" &&
                element.options["products-cart-redirect"] !== "false"))
          }
          placeholder="https://"
        />

        <VariantBuilderSetting
          object={element}
          label="Apply discount code upon redirect"
          setting_name="[options][products-cart-redirect-discount]"
          setting_type="text"
          object_type="elements"
          object_id={element.id}
          value={
            element.options["products-cart-redirect-discount"]
              ? element.options["products-cart-redirect-discount"]
              : ""
          }
          placeholder="BFCMSALE"
          condition={
            ["Shopify", "ShopifyGraphql"].includes(
              element.options["products-source"]
            ) &&
            ["product", "cart"].includes(
              element.options["confirmation-type"]
            ) &&
            element.options["products-button-action-after-add-to-cart"] !==
              "step" &&
            element.options["products-button-action-after-add-to-cart"] !==
              "cta"
          }
        />
      </VariantBuilderSetting>
    );
  };

  return (
    <React.Fragment>
      <div
        className="col-xs-12 sortable-container products-content"
        data-object_type="elements"
        data-object_id={element.id}
      >
        {imageSettings()}

        {element.element_type == "product_hero" && (
          <div
            className="col-xs-12 no-padding-sides"
            style={{ margin: "5px 0px" }}
          >
            <div className="cf-divider-line border-solid border-[0px] border-t-[1px] border-slate-200 dark:border-slate-800" />
          </div>
        )}

        {offerBadgeSettings()}
        {reviewSettings()}
        {titleSettings()}
        {priceSettings()}
        {descriptionSettings()}
        {variantPickerSettings()}
        {quantityPickerSettings()}
        {subscriptionSettings()}
        {removeFromCartSettings()}
        {buttonSettings()}

        {element.element_type == "product_hero" &&
          Object.values(variant.content_toggle_items)
            .filter(
              (item) =>
                item.element_id == element.id && item.toBeDeleted !== true
            )
            .sort((a, b) => a.position - b.position)
            .map((item) => (
              <VariantBuilderSetting
                key={`custom-element-${item.id}`}
                object={item}
                setting_name="[position]"
                setting_type="item_v2"
                object_type="content_toggle_items"
                object_id={item.id}
                title={item.content || "Custom Content"}
                label_icon={"fa-puzzle-piece"}
                label_max_length={40}
                label_hover_max_length={30}
                style={{ margin: "4px 0px" }}
                draggable={"true"}
                position={item.position}
                hide_duplicate={true}
                hide_remove={false}
                toggle_setting_name="[options][show]"
                toggle_value={
                  item.options && item.options["show"]
                    ? item.options["show"]
                    : "true"
                }
                panel_type="modal"
                side_panel={true}
                modalTitle={`Settings - ${item.content || "Custom Content"}`}
              >
                <VariantBuilderSetting
                  object={item}
                  label="Title"
                  setting_name="[content]"
                  setting_type="text"
                  object_type="content_toggle_items"
                  object_id={item.id}
                  value={item.content || ""}
                  placeholder="Custom Content"
                />
              </VariantBuilderSetting>
            ))}
      </div>

      {element.element_type == "product_hero" && (
        <div className="col-xs-12 my-5">
          <button
            onClick={() => {
              const builtinPositions = [
                "offer-badge",
                "reviews",
                "title",
                "price",
                "description",
                "variant-picker",
                "quantity-picker",
                "subscription",
                "button",
              ].map(
                (type) => parseInt(element.options[`${type}-position`]) || 0
              );

              const customPositions = Object.values(
                variant.content_toggle_items
              )
                .filter(
                  (item) =>
                    item.element_id == element.id && item.toBeDeleted !== true
                )
                .map((item) => item.position || 0);

              const maxPosition = Math.max(
                ...builtinPositions,
                ...customPositions,
                0
              );

              builder.create(
                "content_toggle_items",
                {
                  position: maxPosition + 1,
                  element_id: element.id,
                  content: `Custom Content ${customPositions.length + 1}`,
                },
                {
                  callback: (content_toggle_item) => {
                    setContextData((prevData) => {
                      let newContextData = { ...prevData };
                      newContextData.objects.variant.sections[
                        content_toggle_item.section.id
                      ] = content_toggle_item.section;
                      return newContextData;
                    });
                  },
                }
              );
            }}
            className="w-full p-2 text-left bg-transparent border-none text-xl text-blue-600 dark:text-blue-200 dark:hover:text-white"
          >
            <FontAwesomeIcon icon="plus-square" className="mr-2 text-2xl" /> Add
            Custom Content
          </button>
        </div>
      )}
    </React.Fragment>
  );
};

export default ElementSettingsProductContent;
